import api from "../../core/api";
import i18n from "../../core/i18n";
import accountContext from "../../account/accountContext";
import { isResolvedApiImageUrl } from "./botImageUrl";
import {
  SendMessageRequest,
  SendMessageResponse,
  ChatHistoryResponse,
  SetContextRequest,
  SetContextResponse,
  ClearContextResponse,
  BotAccessResponse,
  HealthCheckResponse,
  ActionResultPayload,
  isThinkingModel,
} from "./models/chatBotModels";
import {
  SessionInitResponse,
  ThreadListResponse,
  ThreadDetailResponse,
  ThreadCountsResponse,
} from "../../customer/midas/midasPageModels";

const getRequestConfig = () => {
  return { baseURL: (window as any).app.config.apiBaseUrl };
};

class ChatBotApi {
  /**
   * Send a message and get a complete response (non-streaming).
   * POST /v1/bot/chat
   * When image is provided, sends as multipart/form-data instead of JSON.
   * When action_id is provided, includes it in the request body (action button click).
   * When actionResult is provided (MA-0b.3), includes it for conversation continuity.
   */
  sendMessage(
    message: string,
    machineId?: string | null,
    threadId?: string,
    image?: File,
    actionId?: string,
    actionContext?: Record<string, unknown>,
    actionResult?: ActionResultPayload,
    newThread?: boolean
  ): Promise<SendMessageResponse> {
    if (image) {
      // Multipart form-data for image upload
      const formData = new FormData();
      formData.append("message", message);
      formData.append("machine_id", machineId || "");
      if (threadId) {
        formData.append("thread_id", threadId);
      }
      if (newThread) {
        formData.append("new_thread", "true");
      }
      formData.append("image", image);
      return api
        .post<SendMessageResponse>("/v1/bot/chat", formData, {
          ...getRequestConfig(),
          headers: { "Content-Type": "multipart/form-data" },
        })
        .then((x) => x.data);
    }

    const body: Record<string, any> = { message };
    body.machine_id = machineId || null;
    if (threadId) {
      body.thread_id = threadId;
    }
    if (actionId) {
      body.action_id = actionId;
    }
    if (actionContext) {
      body.action_context = actionContext;
    }
    if (actionResult) {
      body.action_result = actionResult;
    }
    if (newThread) {
      body.new_thread = true;
    }
    return api
      .post<SendMessageResponse>("/v1/bot/chat", body, getRequestConfig())
      .then((x) => x.data);
  }

  /**
   * Send a message via SSE streaming for progress indicators.
   * POST /v1/bot/chat/stream
   *
   * Returns the raw Response object so the caller can read the SSE stream.
   * Uses native fetch (not axios) because we need access to ReadableStream
   * for Server-Sent Events parsing (axios buffers the response).
   *
   * When image is provided, sends as multipart/form-data instead of JSON.
   * When action_id is provided, includes it in the request body (action button click).
   * When actionResult is provided (MA-0b.3), includes it for conversation continuity.
   */
  async streamMessage(
    message: string,
    machineId?: string | null,
    threadId?: string,
    image?: File,
    actionId?: string,
    actionContext?: Record<string, unknown>,
    actionResult?: ActionResultPayload,
    newThread?: boolean,
    /** MC-26: Short friendly text to persist in ChatMessages instead of `message`.
     *  Bot uses the full `message` for prompt/context but stores `display_text`
     *  as the user-visible history. Optional; older bots ignore it. */
    displayText?: string,
    /** MC-27: Structured page hint for (i) button. Adds page_info_request to JSON body.
     *  Only applies on non-multipart (JSON) requests. */
    pageInfoRequest?: {
      page: number;
      collection: string;
      list_id?: string;
    },
    /** MODEL-PICKER (spec §5 + §14.1): per-message model override for the
     *  `.cc` playground. MUST be one of the bot allowlist ids — the closed
     *  6-id `ModelOverrideId` union in chatBotModels (Sonnet 4.6/5/5-thinking,
     *  Opus 4.8/4.8-thinking, Haiku 4.5) — callers pass values from the
     *  closed union only, never free text. Omitted entirely for the default
     *  model (Sonnet 4.6 = absent field; never send ""). Sent ONLY on this
     *  stream endpoint — the sync `POST /v1/bot/chat` silently ignores it,
     *  which is why the stream→sync fallback is disabled for override turns
     *  (chatBotSlice). */
    modelOverride?: string,
    /** MODEL-PICKER-PIPELINE: per-message bot-internal answer-pipeline
     *  override (the "MIDAS v2"/"v3" selector). Legal wire values are "v2" and
     *  "v3" (agentic diagnosis); OFF is expressed by OMITTING the field
     *  entirely (never "", never "v1"). Sibling of model_override on BOTH
     *  stream send paths (JSON + multipart). The sync POST /v1/bot/chat
     *  IGNORES it, so the field is never added there — which is why pipeline
     *  turns share the model picker's disabled stream→sync fallback
     *  (chatBotSlice). Unknown/invalid values are silently defaulted
     *  server-side (HTTP 200); nothing to handle client-side. */
    pipelineOverride?: string,
    /** External abort signal so a caller can cancel the stream (unmount /
     *  next send). When provided it is CHAINED with the internal timeout
     *  controller — aborting either source aborts the fetch. */
    externalSignal?: AbortSignal
  ): Promise<Response> {
    const baseUrl = (window as any).app.config.apiBaseUrl;
    const url = `${baseUrl}/v1/bot/chat/stream`;

    const token = accountContext.accessToken;

    let fetchBody: BodyInit;
    const headers: Record<string, string> = {
      Accept: "text/event-stream",
      "X-UI-Language": localStorage.getItem("i18nextLng") || "sk",
    };
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    if (image) {
      // Multipart form-data for image upload
      // Do NOT set Content-Type — browser sets it with boundary automatically
      const formData = new FormData();
      formData.append("message", message);
      formData.append("machine_id", machineId || "");
      if (threadId) {
        formData.append("thread_id", threadId);
      }
      if (newThread) {
        formData.append("new_thread", "true");
      }
      // MODEL-PICKER: multipart path — string field beside new_thread.
      // Always a string here (closed-union value), so the bot's non-string
      // 422 parse rule can never trip on this path.
      if (modelOverride) {
        formData.append("model_override", modelOverride);
      }
      // MODEL-PICKER-PIPELINE: multipart path — string field beside
      // model_override. No selection = the field is NEVER appended (absent,
      // not "") → bot-side v3 default. The opt-out value ("v1" | "v2") is
      // passed through verbatim.
      if (pipelineOverride) {
        formData.append("pipeline_override", pipelineOverride);
      }
      formData.append("image", image);
      fetchBody = formData;
    } else {
      headers["Content-Type"] = "application/json";
      const body: Record<string, any> = { message };
      body.machine_id = machineId || null;
      if (threadId) {
        body.thread_id = threadId;
      }
      if (actionId) {
        body.action_id = actionId;
      }
      if (actionContext) {
        body.action_context = actionContext;
      }
      if (actionResult) {
        body.action_result = actionResult;
      }
      if (newThread) {
        body.new_thread = true;
      }
      // MC-26: forward friendly-text override so the bot persists the short
      // display version in history instead of the raw info_prompt. Older bots
      // ignore unknown fields.
      if (displayText && displayText.length > 0) {
        body.display_text = displayText;
      }
      // MC-27: forward structured page hint from (i) button click. Bot uses it to
      // skip intent classification + vector search; older bots ignore unknown fields.
      if (pageInfoRequest) {
        body.page_info_request = pageInfoRequest;
      }
      // MODEL-PICKER: JSON path — beside the other optional fields. Unknown
      // STRING values are silently ignored server-side (200, default model);
      // our closed union prevents unknown values in the first place.
      if (modelOverride) {
        body.model_override = modelOverride;
      }
      // MODEL-PICKER-PIPELINE: JSON path — sibling of model_override.
      // No selection = the key is NEVER set (absent, not "") → bot-side v3
      // default. Unknown values are silently defaulted server-side (200); the
      // closed PipelineOverrideId type means only "v1" | "v2" can reach here.
      if (pipelineOverride) {
        body.pipeline_override = pipelineOverride;
      }
      fetchBody = JSON.stringify(body);
    }

    // Abort controller with a timeout to prevent indefinite hanging. When the
    // caller supplies its own `externalSignal` (unmount/next-send cancellation)
    // we chain it: aborting either source aborts the fetch.
    // MODEL-PICKER: thinking-model turns get 3 minutes — extended reasoning is
    // slower by design and the proxy already allows 180s SSE (spec §4/§5).
    // v1.1 (§14.1): keyed on the isThinkingModel() membership set (Sonnet 5
    // thinking + Opus 4.8 thinking) — NOT a single id, NOT a suffix test.
    const timeoutMs = isThinkingModel(modelOverride) ? 180_000 : 90_000;
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
    const onExternalAbort = () => controller.abort();
    if (externalSignal) {
      if (externalSignal.aborted) controller.abort();
      else externalSignal.addEventListener("abort", onExternalAbort);
    }

    try {
      const response = await fetch(url, {
        method: "POST",
        headers,
        body: fetchBody,
        signal: controller.signal,
      });

      if (!response.ok) {
        // Preserve the HTTP status on the thrown error so callers can branch
        // on it (e.g. skip/allow the sync fallback based on the status code).
        const statusErr = new Error(
          `Stream request failed with status ${response.status}`
        ) as Error & { status?: number };
        statusErr.status = response.status;
        throw statusErr;
      }

      return response;
    } catch (err) {
      if (err instanceof DOMException && err.name === "AbortError") {
        throw new Error(
          `Stream request timed out after ${Math.round(timeoutMs / 1000)} seconds`
        );
      }
      throw err;
    } finally {
      clearTimeout(timeoutId);
      if (externalSignal) {
        externalSignal.removeEventListener("abort", onExternalAbort);
      }
    }
  }

  /**
   * Get conversation history.
   * GET /v1/bot/chat/history
   */
  getHistory(limit?: number): Promise<ChatHistoryResponse> {
    const params: Record<string, unknown> = {};
    if (limit !== undefined) {
      params.limit = limit;
    }
    return api
      .get<ChatHistoryResponse>("/v1/bot/chat/history", {
        ...getRequestConfig(),
        params,
      })
      .then((x) => x.data);
  }

  /**
   * Set the machine context for the current session.
   * POST /v1/bot/chat/context
   * Pass machineId=null to exit machine mode (returns ClearContextResponse).
   */
  setMachineContext(machineId: string): Promise<SetContextResponse>;
  setMachineContext(machineId: null): Promise<ClearContextResponse>;
  setMachineContext(
    machineId: string | null
  ): Promise<SetContextResponse | ClearContextResponse> {
    const body: SetContextRequest = { machine_id: machineId };
    return api
      .post<SetContextResponse | ClearContextResponse>(
        "/v1/bot/chat/context",
        body,
        getRequestConfig()
      )
      .then((x) => x.data);
  }

  /**
   * Check if the bot server is healthy and reachable.
   * GET /v1/bot/health
   *
   * Uses native fetch instead of the global axios instance to avoid
   * triggering the global 403 handler (PermissionDeniedNotification).
   * These background checks should fail silently.
   */
  async checkHealth(): Promise<HealthCheckResponse> {
    const baseUrl = (window as any).app.config.apiBaseUrl;
    const token = accountContext.accessToken;
    const headers: Record<string, string> = {
      Accept: "application/json",
      "X-UI-Language": localStorage.getItem("i18nextLng") || "sk",
    };
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }
    const response = await fetch(`${baseUrl}/v1/bot/health`, { headers });
    if (!response.ok) {
      throw new Error(`Health check failed: ${response.status}`);
    }
    return response.json();
  }

  /**
   * Check if the current user/company has access to the bot chat feature.
   * GET /v1/customer/settings/bot-chat
   *
   * Uses native fetch to avoid triggering the global 403 handler.
   * If the user lacks permission, we just return disabled -- no banner.
   */
  async checkBotAccess(): Promise<BotAccessResponse> {
    const baseUrl = (window as any).app.config.apiBaseUrl;
    const token = accountContext.accessToken;
    const headers: Record<string, string> = {
      Accept: "application/json",
      "X-UI-Language": localStorage.getItem("i18nextLng") || "sk",
    };
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }
    const response = await fetch(`${baseUrl}/v1/customer/settings/bot-chat`, {
      headers,
    });
    if (!response.ok) {
      return { enabled: false, globalEnabled: false };
    }
    return response.json();
  }

  /**
   * Update bot chat enabled setting for the current user's company.
   * PUT /v1/customer/settings/bot-chat
   *
   * Only Admin or SystemAdministrator can call this.
   */
  updateBotChatSettings(enabled: boolean): Promise<BotAccessResponse> {
    return api
      .put<BotAccessResponse>(
        "/v1/customer/settings/bot-chat",
        { enabled },
        getRequestConfig()
      )
      .then((x) => x.data);
  }

  /**
   * Fetch an artifact image via the bot image proxy.
   * Returns a blob URL for use in <img> src.
   * The caller is responsible for revoking the blob URL when done.
   * Accepts: relative (/api/v1/image/...) paths and full bot-gateway URLs
   * carrying that marker (both re-proxied through /v1/bot/image/), AND an
   * absolute, already-resolved bot endpoint URL such as the diagram page-image
   * endpoint /v1/bot/diagram/{page}/image (fetched directly with auth).
   */
  async fetchBotImage(relativePath: string): Promise<string> {
    const baseUrl = (window as any).app?.config?.apiBaseUrl || "";
    // Schema/diagram page images are served from a DIFFERENT endpoint than the
    // generic /v1/bot/image/ proxy: the caller builds the page-image URL
    // `${apiBaseUrl}/v1/bot/diagram/{page}/image` up-front (kiosk
    // resolveKioskArtifactImageUrl; desktop MidasArtifactThumbnail). Fetch such a
    // fully-resolved endpoint URL directly. An absolute bot-gateway URL that still
    // carries the /api/v1/image/ marker is re-proxied below, preserving the
    // documented "full bot-gateway URL" input.
    //
    // Shared predicate since 2026-08-19: `apiBaseUrl` is root-relative in prod, so
    // the old `startsWith("http")` test misclassified the diagram URL and
    // double-proxied it. See shared/chatBot/botImageUrl.ts.
    const isResolvedEndpoint = isResolvedApiImageUrl(relativePath);
    let url: string;
    if (isResolvedEndpoint) {
      url = relativePath;
    } else {
      const marker = "/api/v1/image/";
      const markerIdx = relativePath.indexOf(marker);
      const stripped = markerIdx !== -1
        ? relativePath.slice(markerIdx + marker.length)
        : relativePath.replace(/^\/+/, "");
      url = `${baseUrl}/v1/bot/image/${stripped}`;
    }

    const token = accountContext.accessToken;
    const headers: Record<string, string> = {
      "X-UI-Language": localStorage.getItem("i18nextLng") || "sk",
    };
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    const response = await fetch(url, { headers });
    if (!response.ok) {
      throw new Error(`Image fetch failed: ${response.status}`);
    }
    const blob = await response.blob();
    return URL.createObjectURL(blob);
  }

  // ─────────────────────────────────────────────────────────────────────
  // Thread CRUD (M4: Chat History)
  // ─────────────────────────────────────────────────────────────────────

  /**
   * List conversation threads for the current user.
   * GET /v1/bot/chat/threads
   *
   * machineId modes:
   *   - undefined → no param (all threads)
   *   - null      → sends ?machine_id=null (platform-only)
   *   - "guid"    → sends ?machine_id=guid (machine-scoped)
   */
  getThreads(limit?: number, offset?: number, machineId?: string | null): Promise<ThreadListResponse> {
    const params: Record<string, unknown> = {};
    if (limit !== undefined) params.limit = limit;
    if (offset !== undefined) params.offset = offset;
    if (machineId !== undefined) {
      params.machine_id = machineId === null ? "null" : machineId;
    }
    return api
      .get<ThreadListResponse>("/v1/bot/chat/threads", {
        ...getRequestConfig(),
        params,
      })
      .then((x) => x.data);
  }

  /**
   * Get thread counts per machine (batch).
   * POST /v1/bot/chat/threads/counts
   */
  getThreadCounts(machineIds: string[]): Promise<ThreadCountsResponse> {
    return api
      .post<ThreadCountsResponse>(
        "/v1/bot/chat/threads/counts",
        { machine_ids: machineIds },
        getRequestConfig()
      )
      .then((x) => x.data);
  }

  /**
   * Create a new conversation thread.
   * POST /v1/bot/chat/threads
   */
  createThread(machineId?: string): Promise<{ id: string; title: string; created_at: string; machine_id?: string }> {
    // MIDAS-BUG-001 (Part 1): use the localised "New chat" label instead of
    // hardcoded English. The title is replaced by the optimistic thread flow
    // once the user sends their first message (with text or image filename).
    const body: Record<string, any> = {
      title: i18n.t("chatBot:newChat", "New Chat"),
    };
    body.machine_id = machineId || null;
    return api
      .post("/v1/bot/chat/threads", body, getRequestConfig())
      .then((x) => x.data);
  }

  /**
   * Get a single thread with its messages.
   * GET /v1/bot/chat/threads/{id}
   */
  getThread(id: string): Promise<ThreadDetailResponse> {
    return api
      .get<ThreadDetailResponse>(`/v1/bot/chat/threads/${id}`, getRequestConfig())
      .then((x) => x.data);
  }

  /**
   * Rename a conversation thread.
   * PATCH /v1/bot/chat/threads/{id}
   */
  renameThread(id: string, title: string): Promise<{ id: string; title: string }> {
    return api
      .patch(`/v1/bot/chat/threads/${id}`, { title }, getRequestConfig())
      .then((x) => x.data);
  }

  /**
   * Delete a conversation thread.
   * DELETE /v1/bot/chat/threads/{id}
   */
  deleteThread(id: string): Promise<void> {
    return api
      .delete(`/v1/bot/chat/threads/${id}`, getRequestConfig())
      .then(() => undefined);
  }

  /**
   * Initialize a MIDAS full-screen session.
   * POST /v1/bot/chat/session-init
   *
   * Returns greeting, summary numbers, and suggestion chips.
   * When machineId is provided, returns machine-scoped data.
   * Falls back to mock data when the bot endpoint is not available yet.
   */
  async sessionInit(machineId?: string): Promise<SessionInitResponse> {
    // BUG-2026-03-20: Mock responses must NOT include a greeting or suggestions
    // when in machine mode. MidasMachineWelcome has its own localized fallback
    // greeting and suggestion cards. Previously, the English mock "Welcome back."
    // was rendered instead of the localized machine greeting when the API
    // returned 429 (rate limited) or was unavailable.
    const mockResponse: SessionInitResponse = machineId
      ? {
          // Machine mode: empty greeting/suggestions so MidasMachineWelcome
          // falls through to its own i18n-based fallback content
          greeting: "",
          summary: {
            open_service_requests: 0,
            pending_offers: 0,
            unread_notifications: 0,
            machines_count: 0,
            recent_activity: null,
          },
          suggestions: [],
        }
      : {
          greeting: "",
          summary: {
            open_service_requests: 0,
            pending_offers: 0,
            unread_notifications: 0,
            machines_count: 0,
            recent_activity: null,
          },
          suggestions: [],
        };

    try {
      const baseUrl = (window as any).app.config.apiBaseUrl;
      const token = accountContext.accessToken;
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-UI-Language": localStorage.getItem("i18nextLng") || "sk",
      };
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }
      const body: Record<string, any> = {};
      body.machine_id = machineId || null;
      const response = await fetch(`${baseUrl}/v1/bot/chat/session-init`, {
        method: "POST",
        headers,
        body: JSON.stringify(body),
      });
      if (!response.ok) {
        return mockResponse;
      }
      return response.json();
    } catch {
      return mockResponse;
    }
  }
}

export default new ChatBotApi();
